import { useState } from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { Button, Row, Col, Form, Offcanvas } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import NepaliDate from 'nepali-date-converter';
// components
import { FormGroup } from '../../../../../Components/FormGroup/FormGroup';

// helpers
import { fetchWrapper } from '../../../../../_helpers/fetchWrapper';
import { ErrorMessageHandler } from '../../../../../_helpers/_methods';

// icons
import { BsFillCalendarEventFill, BsFillPersonFill } from 'react-icons/bs';
import { useEffect } from 'react';
import FormError from '../../../../../Components/FormError';
import ResourcePersonModal from './ResourcePersonModal';
import SubmitBtn from '../../../../../Components/Buttons/SubmitBtn';
import CancelBtn from '../../../../../Components/Buttons/CancelBtn';
import { AiFillMinusCircle } from 'react-icons/ai';
import { FaBook } from 'react-icons/fa';
import { FormattedMessage } from 'react-intl';
import CourseModal from './CourseModal';

const StyledEventSessionOffcanvas = styled(Offcanvas)`
  .label {
    margin-right: 0.4rem;
  }
  .btn-rounded {
    border-radius: 1000px;
  }
  .offcanvas-title {
    color: #0d6efd;
    font-weight: bold;
  }
  .offcanvas-body {
    overflow: auto;
    height: 90vh;
  }
`;

const Title = styled.h3`
  svg {
    color: #98c9ff;
  }
  span {
    color: #525252;
  }
`;
function EventSessionOffCanvas({ show, editData, handleClose }) {
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm({
    mode: 'onBlur'
  });
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [resourcePersons, setResourcePersons] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const lang = useSelector((state) => state.local.local);

  useEffect(() => {
    const startTime = editData?.start_time.split(':').splice(0, 2).join(':');
    const endTime = editData?.end_time.split(':').splice(0, 2).join(':');

    setStartTime(startTime);
    setEndTime(endTime);
    setValue('start_time', startTime);
    setValue('end_time', endTime);
    setValue('title', editData?.title);
    setValue('description', editData?.description);
    let tempResourcePerson = [];
    if (editData) {
      editData?.resource_persons?.forEach((rp) => {
        let user = {
          id: rp.id,
          fullname: rp.user__fullname,
          image: rp.user__image
        };
        tempResourcePerson.push(user);
      });
      setSelectedCourses(editData?.course);
    }
    setResourcePersons(tempResourcePerson);
  }, [editData]);

  const [showResoucePerson, setShowResourcePerson] = useState(false);
  const [showCourseModal, setShowCourseModal] = useState(false);

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const sessionData = {
        ...data,
        event: id,
        date: show,
        resource_persons: resourcePersons.map((person) => person.id),
        course: selectedCourses
      };
      if (editData) {
        await fetchWrapper.patch(`/event/session/${editData.id}/`, sessionData);
        toast.success(<FormattedMessage id="event.session.updateSuccesful" />);
      } else {
        if (resourcePersons.length > 0) {
          await fetchWrapper.post('/event/session/', sessionData);
          toast.success(<FormattedMessage id="event.session.createSuccesful" />);
        } else {
          setIsLoading(false);
          return toast.error(<FormattedMessage id="event.session.errorAddRP" />);
        }
      }
      handleClose();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      ErrorMessageHandler(error);
    }
  };

  const onStartTimeChange = (e) => {
    setStartTime(e.target.value);
  };

  const onEndTimeChange = (e) => {
    setEndTime(e.target.value);
  };

  useEffect(() => {
    if (startTime && endTime) {
      let startTimeNumber = startTime.replace(':', '.');
      let endTimeNumber = endTime.replace(':', '.');

      if (startTimeNumber === endTimeNumber) {
        setError('end_time', {
          type: 'manual',
          message:
            lang === 'ne'
              ? ' र अन्त्य हुने समय समान हुनुहुँदैन।'
              : 'Start time and End time must not be same.'
        });
        return;
      }
      let totalTime = endTimeNumber - startTimeNumber;
      if (totalTime > 0) {
        clearErrors('start_time');
        clearErrors('end_time');
      } else {
        setError('end_time', {
          type: 'manual',
          message:
            lang === 'ne'
              ? 'अन्त्य समय सन्चालन हुने समय भन्दा ठूलो हुनु हुँदैन।'
              : 'End time must not be greater than start time.'
        });
      }
    }
  }, [startTime, endTime]);

  const handleResourcePersonAdd = (persons) => {
    setResourcePersons([...resourcePersons, ...persons]);
  };
  const handleRemoveResourcePerson = async (personId) => {
    let tempResourcePersons = [...resourcePersons];
    if (editData) {
      try {
        await fetchWrapper.post(`/event/session/${editData.id}/remove-resource-person/`, {
          resource_persons: [personId]
        });
      } catch (error) {
        ErrorMessageHandler(error);
      }
    }
    tempResourcePersons = tempResourcePersons.filter((rp) => rp.id !== personId);
    setResourcePersons(tempResourcePersons);
  };

  const handleRemoveCourses = async (courseId) => {
    let tempSelectedCourses = [...selectedCourses];
    tempSelectedCourses = tempSelectedCourses.filter((course) => course.id !== courseId);
    setSelectedCourses(tempSelectedCourses);
  };

  const handleSelectedCourses = (courses) => {
    setSelectedCourses(courses);
  };
  return (
    <StyledEventSessionOffcanvas
      show={Boolean(show)}
      onHide={handleClose}
      autoFocus={true}
      scroll={true}
      placement="end">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title as="h4">
          <FormattedMessage id="event.session" />
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Offcanvas.Body>
          <Title className="d-flex">
            <BsFillCalendarEventFill />
            {lang === 'ne' ? (
              <span className="ms-2">
                {new NepaliDate(new Date(show)).format('ddd DD, MMMM', 'np')}
              </span>
            ) : (
              <span className="ms-2">{dayjs(show).format('ddd, DD MMM')}</span>
            )}
          </Title>
          <br />
          <Row>
            <Col sm={12}>
              <FormattedMessage id="title.placeholder">
                {(placeholder) => (
                  <FormGroup
                    label={<FormattedMessage id="title" />}
                    id="title"
                    type="text"
                    errorId="event.session.title"
                    register={register}
                    error={errors}
                    required={true}
                    placeholder={placeholder[0]}
                  />
                )}
              </FormattedMessage>
            </Col>
            <Col sm={12}>
              <FormattedMessage id="description.placeholder">
                {(placeholder) => (
                  <FormGroup
                    label={<FormattedMessage id="description" />}
                    id="description"
                    type="textarea"
                    errorId="event.session.description"
                    register={register}
                    error={errors}
                    required={true}
                    placeholder={placeholder[0]}
                  />
                )}
              </FormattedMessage>
            </Col>
            <Col sm={12} className="mt-2 d-flex justify-content-between">
              <span className="label">
                <FormattedMessage id="start_time" />
              </span>
              <span>
                <input
                  value={startTime}
                  {...register('start_time', {
                    required:
                      lang === 'ne' ? 'सन्चालन हुने समय छान्नुहोस्' : 'Start time is required'
                  })}
                  onChange={(e) => {
                    onStartTimeChange(e);
                  }}
                  type="time"
                />
              </span>
              <FormError error={errors['start_time']} />
            </Col>
            <Col sm={12} className="mt-2 d-flex justify-content-between">
              <span className="label">
                <FormattedMessage id="end_time" />
              </span>
              <span>
                <input
                  value={endTime}
                  type="time"
                  {...register('end_time', {
                    required: lang === 'ne' ? 'अन्त्य हुने समय छान्नुहोस्' : 'End time is required'
                  })}
                  onChange={(e) => {
                    onEndTimeChange(e);
                  }}
                />
              </span>
              <FormError error={errors['end_time']} />
            </Col>

            <Col sm={12} className="py-1 px-2 w-100 my-2">
              <span>
                <b>
                  <FormattedMessage id="resource_person" />
                </b>
              </span>
              <div className="bg-muted">
                {resourcePersons.length > 0 ? (
                  resourcePersons.map((resourcePerson, index) => {
                    return (
                      <div
                        key={index}
                        className="d-flex justify-content-between px-1 py-2 border w-100">
                        <BsFillPersonFill />
                        <div>
                          <span className="ml-2">{resourcePerson.fullname}</span>
                        </div>
                        <div onClick={() => handleRemoveResourcePerson(resourcePerson.id)}>
                          <AiFillMinusCircle className="text-danger" />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="small">
                    <FormattedMessage id="no_resource_person_selected" />
                  </div>
                )}
              </div>
              <Button
                variant="outline-danger"
                className="btn-rounded mt-2"
                onClick={() => setShowResourcePerson(true)}>
                <FormattedMessage id="specify_resource_person" />
              </Button>
            </Col>

            <Col sm={12} className="py-1 px-2 w-100 my-2">
              <span>
                <b>{lang === 'ne' ? 'पाठ्यक्रम' : 'Courses'}</b>
              </span>
              <div className="bg-muted">
                {selectedCourses?.length > 0 ? (
                  selectedCourses.map((course) => {
                    return (
                      <div
                        key={course.id}
                        className="d-flex justify-content-between px-1 py-2 border w-100">
                        <FaBook />
                        <div>
                          <span className="ml-2">{course.title}</span>
                        </div>
                        <div onClick={() => handleRemoveCourses(course.id)}>
                          <AiFillMinusCircle className="text-danger" />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="small">
                    <FormattedMessage id="no_course_selected" />
                  </div>
                )}
              </div>
              <Button
                variant="outline-danger"
                className="btn-rounded mt-2"
                onClick={() => setShowCourseModal(true)}>
                <FormattedMessage id="event.session.addCourses" />
              </Button>
            </Col>

            <Col sm="12" className="border-top mt-3 pt-3 d-flex justify-content-between">
              <SubmitBtn isLoading={isLoading} />
              <CancelBtn onClick={handleClose} />
            </Col>
          </Row>
        </Offcanvas.Body>
      </Form>
      {showResoucePerson && (
        <ResourcePersonModal
          resourcePersons={resourcePersons}
          sessionId={editData?.id}
          show={showResoucePerson}
          handleRemoveResourcePerson={handleRemoveResourcePerson}
          action={handleResourcePersonAdd}
          handleClose={() => setShowResourcePerson(false)}
        />
      )}
      {showCourseModal && (
        <CourseModal
          show={showCourseModal}
          selectedCourses={selectedCourses}
          action={handleSelectedCourses}
          handleClose={() => setShowCourseModal(false)}
        />
      )}
    </StyledEventSessionOffcanvas>
  );
}

export default EventSessionOffCanvas;
